import React from 'react';

type PropTypes = {
  variant: string;
  styleClasses?: string;
  children: React.ReactNode;
  textColor?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
} & (
  | React.HTMLAttributes<HTMLHeadingElement>
  | React.HTMLAttributes<HTMLParagraphElement>
);

const CustomTypography: React.FC<PropTypes> = (props: PropTypes): JSX.Element => {
  const { variant, children, styleClasses, textColor, as, ...rest } = props;

  function getClasses(): string {
    switch (variant) {
      case '10ftLargeTitle':
        return `font-bold text-[4.5rem] leading-[4.75rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftTitle1':
        return `font-bold text-[3rem] leading-[3.375rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftTitle2':
        return `font-bold text-[2.25rem] leading-[2.625rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftTitle3':
        return `font-bold text-[2rem] leading-[2.25rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftCaption2':
        return `font-bold text-[1.313rem] leading-[2rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftCaption1':
        return `font-medium text-[1.313rem] leading-[1.563rem] ${styleClasses} ${textColor || ''}`;
      case '10ftCaption3':
        return `font-bold text-[1.313rem] leading-[2rem] ${textColor || ''} ${styleClasses}`;
      case '10ftBody':
        return `font-normal text-[1.625rem] leading-[2rem] ${textColor || 'text-componentSubtle'} ${styleClasses}`;
      case '10ftHeadline':
        return `text-[1.75rem] font-bold leading-[2rem] ${textColor || 'text-componentSubtle'} ${styleClasses}`;
      case '10ftCraveTitle3':
        return `mb-16 text-[1.75rem] font-bold leading-[2rem] ${textColor || 'text-componentEmphasized'} ${styleClasses}`;
      case '10ftCallout':
        return `font-bold text-[1.5rem] leading-[1.875rem] ${styleClasses} ${textColor || ''}`;
      case '10ftFootnote':
        return `text-[1.125rem] font-medium leading-[1.5rem] ${styleClasses} ${textColor || ''}`;
      default:
        return styleClasses || '';
    }
  }

  function getDefaultElement(): string {
    switch (variant) {
      case '10ftLargeTitle':
      case '10ftTitle1':
      case '10ftTitle2':
        return 'h1';
      case '10ftTitle3':
        return 'h3';
      case '10ftCaption2':
      case '10ftCaption1':
      case '10ftCaption3':
      case '10ftBody':
      case '10ftHeadline':
      case '10ftCraveTitle3':
      case '10ftCallout':
      case '10ftFootnote':
        return 'p';
      default:
        return 'h1';
    }
  }

  const className = getClasses();
  const element = as || getDefaultElement();

  return React.createElement(element, { className, ...rest }, children);
};

export default CustomTypography;
